import { useEffect, useRef, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getCurrentUser } from "aws-amplify/auth";
import { gql, useMutation } from "@apollo/client";
import { usePostHog } from "posthog-js/react";
import { useMeta } from "../../../../contexts/MetaContext";
import { AuthEvent } from "../../../../data/auth-events";

export const SETUP_NEW_USER_WITH_EXISTING_REFERRALS = gql(`
  mutation SetupNewUserWithExistingReferrals($userId: ID!) {
    setupNewUserWithExistingReferrals(userId: $userId) {
      appealId
    }
  }
`);

type UseUserSetupOptions = {
  onLoginComplete?: () => void;
};

type UseUserSetupResult = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | undefined;
};

export const useUserSetup = ({
  onLoginComplete,
}: UseUserSetupOptions = {}): UseUserSetupResult => {
  const { authStatus } = useAuthenticator();
  const { hasUserAuthenticated, setHasUserAuthenticated } = useMeta();
  const posthog = usePostHog();
  const setupInProgressRef = useRef(false);
  const [setupError, setSetupError] = useState<Error | undefined>(undefined);

  const [attachNewUserToExistingAppeals, { loading, error: mutationError }] =
    useMutation(SETUP_NEW_USER_WITH_EXISTING_REFERRALS);

  useEffect(() => {
    async function setupUser() {
      if (
        authStatus === "authenticated" &&
        !hasUserAuthenticated &&
        !setupInProgressRef.current &&
        !loading
      ) {
        setupInProgressRef.current = true;

        try {
          const user = await getCurrentUser();
          await attachNewUserToExistingAppeals({
            variables: { userId: user.userId },
          });
          setHasUserAuthenticated(true);

          if (posthog) {
            posthog.capture(AuthEvent.Authenticated);
          }

          if (onLoginComplete) {
            onLoginComplete();
          }
        } catch (err) {
          setSetupError(
            err instanceof Error ? err : new Error("Failed to setup user"),
          );
        } finally {
          setupInProgressRef.current = false;
        }
      }
    }

    setupUser();
  }, [
    authStatus,
    hasUserAuthenticated,
    attachNewUserToExistingAppeals,
    setHasUserAuthenticated,
    posthog,
    onLoginComplete,
    loading,
  ]);

  return {
    isAuthenticated: authStatus === "authenticated" && hasUserAuthenticated,
    isLoading:
      authStatus === "configuring" ||
      loading ||
      (authStatus === "authenticated" && !hasUserAuthenticated),
    error: mutationError || setupError,
  };
};
